import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Check, 
  Crown,
  Sparkles,
  Infinity,
  Clock,
  Zap,
  Radio
} from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    period: '',
    features: [
      '3 podcasts only',
      '3-minute episodes',
      'All 9 expert types',
      'Basic audio quality'
    ],
    cta: 'Current Plan',
    disabled: true
  },
  {
    id: 'monthly',
    name: 'Premium Monthly',
    price: 29.99,
    period: '/month',
    features: [
      '30 premium podcasts per month',
      'Up to 3-minute episodes',
      'Access to all 28 AI expert types',
      'HD audio quality',
      'Priority generation queue',
      'Shareable quote cards',
      'Early access to new features'
    ],
    cta: 'Subscribe Now',
    popular: true
  },
  {
    id: 'yearly',
    name: 'Premium Yearly',
    price: 239.99,
    period: '/year',
    originalPrice: 359.88,
    savings: 'Save 33%',
    features: [
      '30 premium podcasts per month',
      'Up to 3-minute episodes',
      'Access to all 28 AI expert types',
      'HD audio quality',
      'Priority generation queue',
      'Shareable quote cards',
      'Early access to new features'
    ],
    cta: 'Subscribe Now'
  }
];

const SubscriptionPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const { token, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Check for payment status on return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;

    const poll = async (sid, attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;
      if (attempts >= maxAttempts) {
        toast.info('Payment verification timed out. Your subscription will activate shortly.');
        return;
      }
      try {
        const response = await axios.get(`${API}/payments/status/${sid}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.payment_status === 'paid') {
          setPaymentSuccess(true);
          if (refreshUser) await refreshUser();
          toast.success('Subscription activated! Welcome to Premium.');
          window.history.replaceState({}, '', '/subscription');
          fetchStats();
          return;
        }
        setTimeout(() => poll(sid, attempts + 1), pollInterval);
      } catch (error) {
        setTimeout(() => poll(sid, attempts + 1), pollInterval);
      }
    };

    poll(sessionId);
  }, [searchParams, token, refreshUser, fetchStats]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleSubscribe = async (planId) => {
    if (planId === 'free') return;

    setSubscribing(planId);

    try {
      const response = await axios.post(
        `${API}/payments/checkout`,
        { 
          plan: planId,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setSubscribing(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0A0A0B' }}>
        <div className="w-12 h-12 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isPremium = stats?.is_premium;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0A0A0B' }}>
      {/* Header */}
      <header className="border-b px-6 py-4" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white hover:bg-white/5"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Home
          </Button>

          <h1 
            className="text-xl font-medium"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F8FAFC' }}
          >
            Subscription
          </h1>

          <div className="w-20" />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ backgroundColor: 'rgba(109, 40, 217, 0.2)' }}>
            <Crown className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-purple-300">Upgrade to Premium</span>
          </div>
          
          <h1 
            className="text-4xl sm:text-5xl font-light tracking-tight mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F8FAFC' }}
          >
            Unlock Unlimited Potential
          </h1>
          <p className="text-lg max-w-xl mx-auto" style={{ color: '#94A3B8' }}>
            Create longer episodes, generate unlimited podcasts, and access premium features
          </p>
        </div>

        {/* Current Status */}
        {isPremium && (
          <div className="glass-panel rounded-xl p-6 mb-8 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Crown className="w-5 h-5 text-purple-400" />
              <span className="text-lg font-medium text-white">You're a Premium Member!</span>
            </div>
            <p style={{ color: '#94A3B8' }}>
              Enjoy 30 podcasts per month and HD audio quality
            </p>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {PLANS.map((plan, index) => {
            const isCurrentPlan = (plan.id === 'free' && !isPremium) || (plan.id !== 'free' && isPremium);
            
            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative rounded-2xl p-6 ${
                  plan.popular 
                    ? 'border-2 border-purple-500' 
                    : 'border border-white/10'
                }`}
                style={{ backgroundColor: '#141417' }}
                data-testid={`plan-${plan.id}`}
              >
                {plan.popular && (
                  <div 
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: '#6D28D9', color: 'white' }}
                  >
                    Most Popular
                  </div>
                )}

                {plan.savings && (
                  <div 
                    className="absolute -top-3 right-4 px-3 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: '#10B981', color: 'white' }}
                  >
                    {plan.savings}
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-lg font-medium mb-2" style={{ color: '#F8FAFC' }}>
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline gap-1">
                    {plan.originalPrice && (
                      <span className="text-lg line-through" style={{ color: '#64748B' }}>
                        ${plan.originalPrice}
                      </span>
                    )}
                    <span className="text-4xl font-light" style={{ color: '#F8FAFC' }}>
                      ${plan.price}
                    </span>
                    <span style={{ color: '#94A3B8' }}>{plan.period}</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={`${plan.id}-feature-${i}`} className="flex items-center gap-3 text-sm" style={{ color: '#94A3B8' }}>
                      <Check className="w-4 h-4 text-purple-400 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={isCurrentPlan || subscribing === plan.id || isPremium}
                  className={`w-full h-12 ${
                    plan.popular 
                      ? 'bg-purple-600 hover:bg-purple-500 text-white' 
                      : 'bg-white/5 hover:bg-white/10 text-white border border-white/10'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                  data-testid={`subscribe-${plan.id}-btn`}
                >
                  {subscribing === plan.id ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : isCurrentPlan ? (
                    'Current Plan'
                  ) : isPremium ? (
                    'Already Premium'
                  ) : (
                    plan.cta
                  )}
                </Button>
              </motion.div>
            );
          })}
        </div>

        {/* Features Comparison */}
        <div className="glass-panel rounded-2xl p-8">
          <h2 
            className="text-2xl font-medium text-center mb-8"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F8FAFC' }}
          >
            Why Go Premium?
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div 
                className="w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: 'rgba(109, 40, 217, 0.2)' }}
              >
                <Radio className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="font-medium mb-2 text-sm" style={{ color: '#F8FAFC' }}>30 Monthly Podcasts</h3>
              <p className="text-xs" style={{ color: '#94A3B8' }}>
                Generate up to 30 premium AI debates every month
              </p>
            </div>

            <div className="text-center">
              <div 
                className="w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: 'rgba(109, 40, 217, 0.2)' }}
              >
                <Clock className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="font-medium mb-2 text-sm" style={{ color: '#F8FAFC' }}>10-Minute Episodes</h3>
              <p className="text-xs" style={{ color: '#94A3B8' }}>
                Longer, deeper episodes with all 28 AI expert types in HD audio
              </p>
            </div>

            <div className="text-center">
              <div 
                className="w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: 'rgba(109, 40, 217, 0.2)' }}
              >
                <Zap className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="font-medium mb-2 text-sm" style={{ color: '#F8FAFC' }}>Priority Queue</h3>
              <p className="text-xs" style={{ color: '#94A3B8' }}>
                Skip the line — your podcasts generate faster with premium priority
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SubscriptionPage;
